import React, {
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
// Interfaces
import { ICrewBase } from "@vmosolutions/oc-interfaces";
// Material-UI
import { Box, Button, Theme, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
//local imports
import { getCrewBases } from "src/redux/actions/Structure.Actions";
import { ICrewTimeOption, defaultCrewBase } from "src/redux/reducers/CrewScheduleReducer";

import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { IconButton } from "src/components/IconButton/IconButton";
import { updateUserAppSettings } from "src/redux/actions/App.Actions";
import { getCrewGroups, setCrewBase, setCrewGroupFilter } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface ISortFilterCrew {
  timelineContainer?: MutableRefObject<HTMLDivElement>;
  positionHandler: (position: IPositionOptions) => void;
  currentPosition: string;
  positionOptions: IPositionOptions[];
  windowId: string;
  savedFilters?: ISavedCrewScheduleDisplayOptions; // loaded in from localstorage if available.
  setSavedFilters?: (value: ISavedCrewScheduleDisplayOptions) => void;
  setTabValue?: React.Dispatch<React.SetStateAction<string | boolean>>;
  groupSave?: boolean;
}

export interface IPositionOptions {
  value: number;
  key: string;
  label: string;
  id: string;
}

export interface IGroupOption {
  value: string;
  key: string;
  label: string;
}

export interface ISavedCrewScheduleDisplayOptions {
  // displayTimezone: IAirportOption;
  crewBase: ICrewTimeOption;
  groupFilter: string[];
  position: IPositionOptions;
}

const selectCrewPositionStyles = (theme: Theme): any => ({
  display: "flex",
  alignItems: "center",
  padding: "10px",
  flexWrap: "wrap",
  "& .base_filter, & .position_filter, & .group_filter": {
    margin: "0px 10px",
    width: 250,
    "& .input_control label": {
      width: "auto",
      margin: "0px 10px auto auto",
      ...theme.typography.subtitle1,
    },
  },
});

/**
 * Crew Schedule Sort and Filter Component
 * Allows filtering the crew schedule
 * @param props -
 */
export const CrewFilterAndSort = forwardRef((props: ISortFilterCrew, ref: any): JSX.Element => {
  const {
    timelineContainer,
    positionHandler,
    currentPosition,
    positionOptions,
    windowId,
    savedFilters,
    setSavedFilters,
    setTabValue,
    groupSave,
  } = props;
  const theme = useTheme();
  const dispatch = useDispatch();

  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[windowId]);
  const crewBase = windowProperties?.crewBase ?? defaultCrewBase;
  const [crewBases, setCrewBases] = useState<ICrewBase[]>([]);

  // crew group filter selector and stateful crewgroups array
  const crewGroupFilter: string[] = useSelector((state: IRootState) => state.crewSchedule.groupFilter[windowId]);
  const [crewGroups, setCrewGroups] = useState<string[]>([]);

  const selectedPosition = useMemo(() => {
    return positionOptions.find((pos: IPositionOptions) => pos.label === currentPosition);
  }, [positionOptions, currentPosition]);

  // This is based on what is currently loaded in redux for this particular window id.
  const currentCrewFilters = useMemo(
    () => ({
      // Base Selection
      crewBase: windowProperties?.crewBase,
      // Group Selection
      groupFilter: crewGroupFilter,
      // Position Selection
      position: selectedPosition,
    }),
    [crewGroupFilter, selectedPosition, windowProperties]
  );

  // compare the savedFilters (loaded from localstorage) to their current set values.
  const matchesSaved = useMemo(() => {
    // console.log(savedFilters, currentCrewFilters);
    const defaultFilters: ISavedCrewScheduleDisplayOptions = {
      crewBase: undefined,
      groupFilter: undefined,
      position: undefined,
    };
    return _.isEqual({ ...defaultFilters, ...savedFilters }, currentCrewFilters);
  }, [currentCrewFilters, savedFilters]);

  /**
   * Function used to store all display options in localstorage *(eventually db) so they are set as default for all windows.
   */
  const saveAsMyDefault = useCallback(async () => {
    // localStorage.setItem("crew-schedule-filters", JSON.stringify(currentCrewFilters));
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Saving Crew Filter/Sort as Default...",
      successMessage: "Default Crew Filter/Sort Saved Successfully.",
      errorMessage: "Error: Unable to Save Crew Filter/Sort",
    };
    const response = await AmplifyAPIWrapper.Put<any>(
      "/me/settings",
      {
        crewScheduleFilters: currentCrewFilters,
      },
      !groupSave && notifications
    );
    dispatch(updateUserAppSettings(response, "crewScheduleFilters"));
    setSavedFilters(currentCrewFilters);
  }, [currentCrewFilters, dispatch, setSavedFilters, groupSave]);

  useImperativeHandle(ref, () => ({
    saveDefault() {
      return currentCrewFilters;
    },
  }));

  useEffect(() => {
    const getCrewBasesAction = async (): Promise<void> => {
      const retrievedCrewBases = await getCrewBases();
      setCrewBases(retrievedCrewBases);
    };
    getCrewBasesAction();
  }, []);

  useEffect(() => {
    positionHandler(selectedPosition);
  }, [positionHandler, selectedPosition]);

  // Get the available crew groups upon render
  useEffect(() => {
    getCrewGroups()
      .then((data: string[]) => setCrewGroups(data ?? []))
      .catch(() => setCrewGroups([]));
  }, []);

  // options for the selectbox for groups.
  const groupOptions: IGroupOption[] = useMemo(() => {
    if (!crewGroups || crewGroups.length === 0) {
      return [];
    }
    const options = crewGroups
      .map((group: string) => ({ value: group, key: group, label: group }))
      .sort((a, b) => {
        return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
      });
    // if theres a group selected, add a "Clear Selection" option to the top of the list
    // if (selectedCrewGroup) options.unshift({ value: "", key: "", label: "Clear Selection" });
    return options;
  }, [crewGroups]);

  const selectedCrewGroups = useMemo(() => {
    // if theres no crew group filters set, selection is null
    if (!crewGroupFilter || groupOptions?.length === 0) return [];
    return groupOptions?.filter((option) => crewGroupFilter.includes(option.value));
    // const selectedGroup = crewGroupFilter[0]; // set to the first until we want to implement multiple group selection
    // return { value: selectedGroup, key: selectedGroup, label: selectedGroup };
  }, [crewGroupFilter, groupOptions]);

  const baseOptions: ICrewTimeOption[] = useMemo(() => {
    if (!crewBases || crewBases.length === 0) {
      return [];
    }
    const options = crewBases
      .filter((crewBase: ICrewBase) => crewBase.isActive === undefined || crewBase.isActive === true)
      .map((crewBase: ICrewBase) => {
        return {
          value: { isUTC: false, id: crewBase._id, timezoneId: crewBase.timezoneId },
          key: crewBase.ICAO,
          label: crewBase.ICAO + " - " + crewBase.name.toUpperCase(),
        };
      })
      .sort((a, b) => {
        return a.label.toUpperCase().localeCompare(b.label.toUpperCase());
      });
    return [defaultCrewBase, ...options];
  }, [crewBases]);

  const [parentElement, setParentElement] = React.useState<HTMLDivElement>(null);

  // set parent element for selection menu portals.
  useEffect(() => {
    if (!!timelineContainer && !!timelineContainer.current) {
      setParentElement(timelineContainer.current);
    }
  }, [timelineContainer]);

  const crewBaseFilterHandler = (base: ICrewTimeOption): void => {
    dispatch(setCrewBase(base, windowId));
  };

  const crewGroupFilterHandler = (groups: IGroupOption[]): void => {
    // Will need to adapt to multi-select later, but the redux portion uses an array of strings.
    const groupValues = groups.map((g) => g.value);
    dispatch(setCrewGroupFilter(groupValues, windowId));
  };

  return (
    <Box sx={selectCrewPositionStyles(theme)}>
      {setTabValue && (
        <IconButton style={{ paddingBottom: "4px" }} onClick={() => setTabValue(false)}>
          <VisibilityOffIcon fontSize="small" />
        </IconButton>
      )}
      <Typography variant="subtitle1" className="body_text" style={{ margin: "0px 20px" }}>
        Filter Crew
      </Typography>
      <SelectField
        className="base_filter"
        label="Base"
        name="sortingMethod"
        closeMenuOnSelect={true}
        hideSelectedOptions={false}
        options={baseOptions}
        onChange={crewBaseFilterHandler}
        value={crewBase}
        menuPortalTarget={parentElement}
        menuPosition="fixed"
        maxMenuHeight={400}
      />
      <SelectField
        className="group_filter"
        label="Group"
        name="group"
        value={selectedCrewGroups}
        options={groupOptions}
        onChange={crewGroupFilterHandler}
        isMulti={true}
        isClearable={true}
        menuPosition="fixed"
        maxOptionsShown={2}
        maxMenuHeight={400}
        multiMode="checkbox"
        placeholder={
          selectedCrewGroups?.length === 0
            ? "Select Groups"
            : selectedCrewGroups?.length === 1
              ? selectedCrewGroups[0].label
              : "Multiple Selected..."
        }
      />
      <SelectField
        className="position_filter"
        label="Position"
        name="position"
        options={positionOptions}
        onChange={(event: any) => positionHandler(event)}
        menuPosition="fixed"
        maxMenuHeight={400}
        value={selectedPosition}
      />
      {!groupSave && (
        <Button
          id={"deselect_btn"}
          disabled={matchesSaved}
          variant="outlined"
          onClick={saveAsMyDefault}
          className="save_as_default_filter"
        >
          {matchesSaved ? "Saved as default" : "Save as my default"}
        </Button>
      )}
    </Box>
  );
});
